/**
 * VÉRAULT Portal — Planning Studio Library (Wave 31 · Track B)
 * ============================================================
 * The advisor's reusable knowledge library.  Separate from the
 * per-trip Planning workbench — this is advisor-global content that
 * spans every journey.
 *
 * Surfaces:
 *   · Destinations sidebar (NYC, Antigua, Paris…)
 *   · Experience library grid with Save/Duplicate/Favorite/Archive
 *   · Day Templates list (save full day from a real trip, clone elsewhere)
 *   · Archive (soft-deleted)
 *   · Favorites + Recently Used
 *   · Global search bar
 *   · "Add to Day/Trip" picker → spawns into Itinerary editor
 *
 * Single Source of Truth: the spawned itinerary item carries
 * ``studio_experience_id`` so the editorial copy traces back.
 */
import React from "react";
import { motion } from "framer-motion";
import {
  Search,
  Plus,
  Star,
  Archive as ArchiveIcon,
  Copy,
  RotateCcw,
  Trash2,
  Send,
  Bookmark,
  MapPin,
  X,
  ChevronRight,
  Library as LibraryIcon,
  Layers,
  Compass,
  Sparkles,
  Users,
  History as HistoryIcon,
  RefreshCw,
} from "lucide-react";
import {
  Surface,
  PageHeader,
  EditorialRule,
  Empty,
  Skeleton,
  PortalButton,
  api,
  motion as portalMotion,
} from "../../index";

const { fadeInUp, stagger } = portalMotion;

const MODES = [
  { id: "library", label: "Experience Library", icon: LibraryIcon },
  { id: "day-templates", label: "Day Templates", icon: Layers },
  { id: "trip-templates", label: "Trip Templates", icon: Compass },
  { id: "favorites", label: "Favorites", icon: Star },
  { id: "recent", label: "Recently Used", icon: Bookmark },
  { id: "archive", label: "Archive", icon: ArchiveIcon },
];

// ─── Top-level shell ───────────────────────────────────────────────

export function PlanningStudioLibrary() {
  const [mode, setMode] = React.useState("library");
  const [query, setQuery] = React.useState("");
  const [debouncedQuery, setDebouncedQuery] = React.useState("");
  const [showCreate, setShowCreate] = React.useState(false);
  const [addToDay, setAddToDay] = React.useState(null); // { experienceId, name } or null
  const [refreshKey, setRefreshKey] = React.useState(0);

  React.useEffect(() => {
    const t = setTimeout(() => setDebouncedQuery(query.trim()), 250);
    return () => clearTimeout(t);
  }, [query]);

  const bump = () => setRefreshKey((k) => k + 1);

  const isSearch = debouncedQuery.length > 0;

  return (
    <motion.div initial="initial" animate="animate" variants={stagger} className="space-y-8">
      <motion.div variants={fadeInUp}>
        <PageHeader
          eyebrow="Planning Studio · Library"
          title="Curate once, deploy across every journey."
          lede="Save experiences, build day templates, and pull them into any trip with one click."
        />
      </motion.div>

      {/* Search + create */}
      <motion.div variants={fadeInUp} className="flex flex-wrap items-center gap-3">
        <div className="relative flex-1 min-w-[280px]">
          <Search
            className="absolute left-3 top-1/2 -translate-y-1/2 size-4 text-[hsl(var(--foreground-muted))]"
            strokeWidth={1.5}
          />
          <input
            type="text"
            placeholder="Search experiences, templates, tags…"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            data-testid="planning-studio-search-input"
            className="w-full pl-10 pr-10 py-2.5 rounded-lg bg-white border border-[hsl(var(--surface-hairline))]/40 font-serif text-sm placeholder:text-[hsl(var(--foreground-muted))] focus:outline-none focus:border-[hsl(var(--accent-gold))]/50 transition-colors"
          />
          {query && (
            <button
              type="button"
              onClick={() => setQuery("")}
              data-testid="planning-studio-search-clear"
              aria-label="Clear search"
              className="absolute right-3 top-1/2 -translate-y-1/2 text-[hsl(var(--foreground-muted))] hover:text-[hsl(var(--foreground))]"
            >
              <X className="size-4" strokeWidth={1.5} />
            </button>
          )}
        </div>
        <PortalButton
          variant="primary"
          onClick={() => setShowCreate(true)}
          data-testid="planning-studio-new-experience"
        >
          <Plus className="size-4" strokeWidth={1.5} />
          <span>New Experience</span>
        </PortalButton>
      </motion.div>

      {/* Mode switch */}
      {!isSearch && (
        <motion.div variants={fadeInUp}>
          <Surface variant="paper" padded={false} className="overflow-hidden">
            <div
              className="grid grid-cols-2 md:grid-cols-6 divide-y md:divide-y-0 md:divide-x divide-[rgba(0,0,0,0.05)]"
              data-testid="planning-studio-mode-bar"
            >
              {MODES.map((m) => {
                const Icon = m.icon;
                const active = mode === m.id;
                return (
                  <button
                    key={m.id}
                    type="button"
                    onClick={() => setMode(m.id)}
                    data-testid={`planning-studio-mode-${m.id}`}
                    className={`relative text-left p-4 transition-colors ${active ? "bg-white" : "hover:bg-white/60"}`}
                  >
                    <div className="flex items-center gap-2.5">
                      <Icon
                        className={`size-4 ${active ? "text-[hsl(var(--accent-gold))]" : "text-[hsl(var(--foreground-muted))]"}`}
                        strokeWidth={1.5}
                      />
                      <span
                        className={`font-mono text-[10.5px] uppercase tracking-[0.22em] ${
                          active ? "text-[hsl(var(--foreground))]" : "text-[hsl(var(--foreground-muted))]"
                        }`}
                      >
                        {m.label}
                      </span>
                    </div>
                    {active && (
                      <span
                        className="absolute -bottom-px left-4 right-4 h-px bg-[hsl(var(--accent-gold))]"
                        aria-hidden
                      />
                    )}
                  </button>
                );
              })}
            </div>
          </Surface>
        </motion.div>
      )}

      <motion.div variants={fadeInUp}>
        {isSearch ? (
          <SearchResults
            key={`search-${debouncedQuery}-${refreshKey}`}
            query={debouncedQuery}
            onAddToDay={setAddToDay}
            onRefresh={bump}
          />
        ) : mode === "library" ? (
          <LibraryView
            key={`library-${refreshKey}`}
            onAddToDay={setAddToDay}
            onRefresh={bump}
          />
        ) : mode === "day-templates" ? (
          <DayTemplatesView key={`tpls-${refreshKey}`} onRefresh={bump} />
        ) : mode === "trip-templates" ? (
          <TripTemplatesView key={`trip-tpls-${refreshKey}`} onRefresh={bump} />
        ) : mode === "favorites" ? (
          <FavoritesView
            key={`favs-${refreshKey}`}
            onAddToDay={setAddToDay}
            onRefresh={bump}
          />
        ) : mode === "recent" ? (
          <RecentlyUsedView
            key={`recent-${refreshKey}`}
            onAddToDay={setAddToDay}
          />
        ) : (
          <ArchiveView key={`arch-${refreshKey}`} onRefresh={bump} />
        )}
      </motion.div>

      {showCreate && (
        <ExperienceFormDialog
          onClose={() => setShowCreate(false)}
          onSaved={() => {
            setShowCreate(false);
            bump();
          }}
        />
      )}
      {addToDay && (
        <AddToDayDialog
          experience={addToDay}
          onClose={() => setAddToDay(null)}
          onAdded={() => {
            setAddToDay(null);
            bump();
          }}
        />
      )}
    </motion.div>
  );
}

// ─── Library view — destinations sidebar + experience grid ─────────

function LibraryView({ onAddToDay, onRefresh }) {
  const [destinations, setDestinations] = React.useState(null);
  const [selectedDest, setSelectedDest] = React.useState(null);
  const [experiences, setExperiences] = React.useState(null);

  React.useEffect(() => {
    api.get("/planning-studio/destinations")
      .then(({ data }) => setDestinations(data.destinations || []))
      .catch(() => setDestinations([]));
  }, []);

  React.useEffect(() => {
    const params = selectedDest ? `?destination=${encodeURIComponent(selectedDest)}` : "";
    api.get(`/planning-studio/experiences${params}`)
      .then(({ data }) => setExperiences(data.experiences || []))
      .catch(() => setExperiences([]));
  }, [selectedDest]);

  return (
    <div className="grid grid-cols-1 md:grid-cols-[220px,1fr] gap-6">
      {/* Destinations rail */}
      <aside data-testid="planning-studio-destinations" className="space-y-1">
        <EditorialRule label="Destinations" />
        <button
          type="button"
          onClick={() => setSelectedDest(null)}
          data-testid="dest-all"
          className={`w-full text-left px-3 py-2 rounded-md font-serif text-sm transition-colors ${
            selectedDest === null
              ? "bg-white text-[hsl(var(--foreground))]"
              : "text-[hsl(var(--foreground-muted))] hover:bg-white/60"
          }`}
        >
          All destinations
        </button>
        {destinations === null ? (
          <Skeleton className="h-10" />
        ) : destinations.length === 0 ? (
          <p className="text-[11px] text-[hsl(var(--foreground-muted))] px-3 py-2">
            Save your first experience to start a collection.
          </p>
        ) : (
          destinations.map((d) => (
            <button
              key={d.destination}
              type="button"
              onClick={() => setSelectedDest(d.destination === "(unfiled)" ? "" : d.destination)}
              data-testid={`dest-${(d.destination || "unfiled").toLowerCase().replace(/\s+/g, "-")}`}
              className={`w-full text-left px-3 py-2 rounded-md font-serif text-sm flex items-center justify-between gap-3 transition-colors ${
                selectedDest === d.destination
                  ? "bg-white text-[hsl(var(--foreground))]"
                  : "text-[hsl(var(--foreground-muted))] hover:bg-white/60"
              }`}
            >
              <span className="flex items-center gap-2">
                <MapPin className="size-3.5" strokeWidth={1.5} />
                {d.destination}
              </span>
              <span className="font-mono text-[10px] tabular-nums">{d.count}</span>
            </button>
          ))
        )}
      </aside>

      {/* Grid */}
      <div>
        <EditorialRule
          label={
            experiences === null
              ? "loading"
              : `${experiences.length} experience${experiences.length === 1 ? "" : "s"}`
          }
        />
        <ExperienceGrid
          experiences={experiences}
          onAddToDay={onAddToDay}
          onRefresh={onRefresh}
        />
      </div>
    </div>
  );
}

// ─── Favorites + Recently Used ─────────────────────────────────────

function FavoritesView({ onAddToDay, onRefresh }) {
  const [items, setItems] = React.useState(null);
  React.useEffect(() => {
    api.get("/planning-studio/favorites")
      .then(({ data }) => setItems(data.experiences || []))
      .catch(() => setItems([]));
  }, []);
  return (
    <div>
      <EditorialRule
        label={items === null ? "loading" : `${items.length} favorite${items.length === 1 ? "" : "s"}`}
      />
      <ExperienceGrid experiences={items} onAddToDay={onAddToDay} onRefresh={onRefresh} />
    </div>
  );
}

function RecentlyUsedView({ onAddToDay }) {
  const [items, setItems] = React.useState(null);
  React.useEffect(() => {
    api.get("/planning-studio/recently-used")
      .then(({ data }) => setItems(data.experiences || []))
      .catch(() => setItems([]));
  }, []);
  return (
    <div>
      <EditorialRule label={items === null ? "loading" : "Most recently used"} />
      <ExperienceGrid experiences={items} onAddToDay={onAddToDay} />
    </div>
  );
}

// ─── Archive ───────────────────────────────────────────────────────

function ArchiveView({ onRefresh }) {
  const [items, setItems] = React.useState(null);

  const load = () => {
    api.get("/planning-studio/archive")
      .then(({ data }) => setItems(data.experiences || []))
      .catch(() => setItems([]));
  };
  React.useEffect(load, []);

  const onRestore = async (id) => {
    await api.post(`/planning-studio/experiences/${id}/restore`);
    load();
    onRefresh?.();
  };
  const onHardDelete = async (id) => {
    if (!window.confirm("Permanently delete this experience? This cannot be undone.")) return;
    await api.delete(`/planning-studio/experiences/${id}`);
    load();
    onRefresh?.();
  };

  if (items === null) return <Skeleton className="h-32" />;
  if (items.length === 0)
    return <Empty title="Nothing in the archive." icon={<ArchiveIcon className="size-6" strokeWidth={1.25} />} />;

  return (
    <div className="space-y-2" data-testid="planning-studio-archive-list">
      {items.map((e) => (
        <div
          key={e.id}
          className="flex items-center gap-3 px-4 py-3 rounded-lg bg-white border border-[hsl(var(--surface-hairline))]/40"
        >
          <div className="flex-1">
            <p className="font-serif text-sm text-[hsl(var(--foreground))]">{e.name}</p>
            <p className="font-mono text-[10px] uppercase tracking-[0.18em] text-[hsl(var(--foreground-muted))]">
              {e.destination || "unfiled"}
            </p>
          </div>
          <button
            type="button"
            onClick={() => onRestore(e.id)}
            data-testid={`archive-restore-${e.id}`}
            className="font-mono text-[10px] uppercase tracking-[0.18em] px-3 py-1.5 rounded-full border border-[hsl(var(--surface-hairline))]/50 text-[hsl(var(--foreground))] hover:bg-[hsl(var(--background-paper))]/70 transition-colors flex items-center gap-1.5"
          >
            <RotateCcw className="size-3" strokeWidth={1.5} /> Restore
          </button>
          <button
            type="button"
            onClick={() => onHardDelete(e.id)}
            data-testid={`archive-delete-${e.id}`}
            className="font-mono text-[10px] uppercase tracking-[0.18em] px-3 py-1.5 rounded-full text-[hsl(var(--foreground-muted))] hover:text-[hsl(var(--accent-terracotta,15_70%_55%))] transition-colors flex items-center gap-1.5"
          >
            <Trash2 className="size-3" strokeWidth={1.5} /> Delete forever
          </button>
        </div>
      ))}
    </div>
  );
}


// ─── Trip Templates (Wave 31 · Track B Phase 2) ───────────────────

function TripTemplatesView({ onRefresh }) {
  const [items, setItems] = React.useState(null);
  const [cloneTpl, setCloneTpl] = React.useState(null);
  const [historyTpl, setHistoryTpl] = React.useState(null);
  const [refreshTpl, setRefreshTpl] = React.useState(null);

  const load = () => {
    api.get("/planning-studio/trip-templates")
      .then(({ data }) => setItems(data.trip_templates || []))
      .catch(() => setItems([]));
  };
  React.useEffect(load, []);

  const onArchive = async (id) => {
    await api.post(`/planning-studio/trip-templates/${id}/archive`);
    load();
    onRefresh?.();
  };
  const onFavorite = async (id, current) => {
    await api.post(`/planning-studio/trip-templates/${id}/favorite`,
      { favorited: !current });
    load();
  };
  const onToggleScope = async (id, currentScope) => {
    const next = currentScope === "agency" ? "private" : "agency";
    await api.post(`/planning-studio/trip-templates/${id}/scope`, { scope: next });
    load();
    onRefresh?.();
  };

  if (items === null) return <Skeleton className="h-32" />;

  return (
    <div data-testid="planning-studio-trip-templates" className="space-y-3">
      <Surface variant="paper" className="p-4 flex items-center gap-3">
        <Compass className="size-4 text-[hsl(var(--accent-gold))]" strokeWidth={1.5} />
        <div className="flex-1">
          <p className="font-serif text-sm text-[hsl(var(--foreground))]">
            Save every event block from every day of a trip as a multi-day framework.
          </p>
          <p className="font-mono text-[10px] uppercase tracking-[0.18em] text-[hsl(var(--foreground-muted))] mt-1">
            Use the "Save trip as template" action inside any journey's Executive Office.
          </p>
        </div>
      </Surface>
      {items.length === 0 ? (
        <Empty title="No trip templates saved yet." icon={<Compass className="size-6" strokeWidth={1.25} />} />
      ) : (
        items.map((t) => {
          const isOwner = t.is_owner !== false; // back-compat: missing flag → owner
          const fav = t.effective_favorited ?? t.favorited;
          const isAgency = t.scope === "agency";
          return (
            <div
              key={t.id}
              data-testid={`trip-template-row-${t.id}`}
              className="px-4 py-3 rounded-lg bg-white border border-[hsl(var(--surface-hairline))]/40 flex items-center gap-3 flex-wrap"
            >
              <button
                type="button"
                onClick={() => onFavorite(t.id, fav)}
                data-testid={`trip-template-fav-${t.id}`}
                aria-label={fav ? "Unfavorite" : "Favorite"}
                className={`size-7 grid place-items-center rounded-full transition-colors ${
                  fav
                    ? "text-[hsl(var(--accent-gold))]"
                    : "text-[hsl(var(--foreground-muted))] hover:text-[hsl(var(--accent-gold))]"
                }`}
              >
                <Star className="size-4" strokeWidth={1.5} fill={fav ? "currentColor" : "none"} />
              </button>
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2 flex-wrap">
                  <p className="font-serif text-sm text-[hsl(var(--foreground))]">{t.name}</p>
                  <button
                    type="button"
                    onClick={() => setHistoryTpl(t)}
                    data-testid={`trip-template-version-${t.id}`}
                    title="Version history"
                    className="font-mono text-[9px] uppercase tracking-[0.18em] px-2 py-0.5 rounded-full bg-[hsl(var(--foreground))]/8 text-[hsl(var(--foreground-muted))] border border-[hsl(var(--surface-hairline))]/40 hover:text-[hsl(var(--foreground))] transition-colors"
                  >
                    v{t.version || 1}
                  </button>
                  {!isOwner && t.owner_name && (
                    <span
                      data-testid={`trip-template-owner-badge-${t.id}`}
                      className="font-mono text-[9px] uppercase tracking-[0.18em] px-2 py-0.5 rounded-full bg-[hsl(var(--accent-gold))]/12 text-[hsl(var(--accent-gold))] border border-[hsl(var(--accent-gold))]/30"
                    >
                      Agency · {t.owner_name}
                    </span>
                  )}
                  {isOwner && isAgency && (
                    <span
                      data-testid={`trip-template-shared-badge-${t.id}`}
                      className="font-mono text-[9px] uppercase tracking-[0.18em] px-2 py-0.5 rounded-full bg-[hsl(var(--foreground))]/8 text-[hsl(var(--foreground-muted))] border border-[hsl(var(--surface-hairline))]/40"
                    >
                      Shared with agency
                    </span>
                  )}
                </div>
                <p className="font-mono text-[10px] uppercase tracking-[0.18em] text-[hsl(var(--foreground-muted))] mt-0.5">
                  {t.destination || "unfiled"} · {t.length_in_days || (t.days || []).length} days · used {t.usage_count || 0}×
                </p>
              </div>
              {isOwner && t.source_trip_id && (
                <button
                  type="button"
                  onClick={() => setRefreshTpl(t)}
                  data-testid={`trip-template-refresh-${t.id}`}
                  title="Re-snapshot from source trip · bumps version"
                  className="font-mono text-[10px] uppercase tracking-[0.18em] px-3 py-1.5 rounded-full text-[hsl(var(--foreground-muted))] hover:text-[hsl(var(--foreground))] transition-colors flex items-center gap-1.5"
                >
                  <RefreshCw className="size-3" strokeWidth={1.5} /> Refresh
                </button>
              )}
              {isOwner && (
                <button
                  type="button"
                  onClick={() => onToggleScope(t.id, t.scope)}
                  data-testid={`trip-template-scope-${t.id}`}
                  aria-label={isAgency ? "Make private" : "Share with agency"}
                  className="font-mono text-[10px] uppercase tracking-[0.18em] px-3 py-1.5 rounded-full text-[hsl(var(--foreground-muted))] hover:text-[hsl(var(--foreground))] transition-colors flex items-center gap-1.5"
                >
                  {isAgency ? "Make private" : "Share with agency"}
                </button>
              )}
              <button
                type="button"
                onClick={() => setCloneTpl(t)}
                data-testid={`trip-template-clone-${t.id}`}
                className="font-mono text-[10px] uppercase tracking-[0.18em] px-3 py-1.5 rounded-full bg-[hsl(var(--accent-gold))]/12 text-[hsl(var(--accent-gold))] border border-[hsl(var(--accent-gold))]/30 hover:bg-[hsl(var(--accent-gold))]/20 transition-colors flex items-center gap-1.5"
              >
                <Send className="size-3" strokeWidth={1.5} /> Clone to trip
              </button>
              {isOwner && (
                <button
                  type="button"
                  onClick={() => onArchive(t.id)}
                  data-testid={`trip-template-archive-${t.id}`}
                  className="font-mono text-[10px] uppercase tracking-[0.18em] px-3 py-1.5 rounded-full text-[hsl(var(--foreground-muted))] hover:text-[hsl(var(--foreground))] transition-colors flex items-center gap-1.5"
                >
                  <ArchiveIcon className="size-3" strokeWidth={1.5} /> Archive
                </button>
              )}
            </div>
          );
        })
      )}
      {cloneTpl && (
        <CloneTripTemplateDialog
          template={cloneTpl}
          onClose={() => setCloneTpl(null)}
          onDone={() => { setCloneTpl(null); load(); onRefresh?.(); }}
        />
      )}
      {historyTpl && (
        <TripTemplateVersionDialog
          template={historyTpl}
          onClose={() => setHistoryTpl(null)}
        />
      )}
      {refreshTpl && (
        <RefreshTripTemplateDialog
          template={refreshTpl}
          onClose={() => setRefreshTpl(null)}
          onDone={() => { setRefreshTpl(null); load(); onRefresh?.(); }}
        />
      )}
    </div>
  );
}

function CloneTripTemplateDialog({ template, onClose, onDone }) {
  const [trips, setTrips] = React.useState(null);
  const [tripId, setTripId] = React.useState("");
  const [startDay, setStartDay] = React.useState(1);
  const [overwrite, setOverwrite] = React.useState(false);
  const [busy, setBusy] = React.useState(false);
  const [err, setErr] = React.useState("");
  const [success, setSuccess] = React.useState(null); // { days_applied, days_skipped } or null
  const sourceTripId = template?.source_trip_id || null;

  React.useEffect(() => {
    api.get("/travels/trips")
      .then(({ data }) => {
        const raw = Array.isArray(data) ? data : data.trips || [];
        // Filter the source trip out — cloning a template back into its
        // own trip silently duplicates day blocks.
        const list = raw.filter((t) => t.id !== sourceTripId);
        setTrips(list);
        if (list.length > 0) setTripId(list[0].id);
      })
      .catch(() => setTrips([]));
  }, [sourceTripId]);

  const submit = async (ev) => {
    ev.preventDefault();
    if (!tripId) {
      setErr("Pick a trip.");
      return;
    }
    setBusy(true);
    setErr("");
    try {
      const { data } = await api.post(
        `/planning-studio/trip-templates/${template.id}/clone-to-trip`,
        { trip_id: tripId, start_day_number: Number(startDay) || 1, overwrite }
      );
      setSuccess({ days_applied: data.days_applied, days_skipped: data.days_skipped });
      // Auto-close after a moment so the family sees the calm confirmation.
      setTimeout(() => { onDone?.(); }, 1600);
    } catch (e) {
      setErr(e?.response?.data?.detail || "Could not clone.");
    } finally {
      setBusy(false);
    }
  };

  return (
    <DialogShell title={`Clone "${template.name}" to a trip`} onClose={onClose} testid="clone-trip-template-dialog">
      {success ? (
        <div className="space-y-3 py-2" data-testid="clone-trip-template-success">
          <p className="font-serif text-sm text-[hsl(var(--foreground))]">
            ✓ Cloned {success.days_applied} day{success.days_applied === 1 ? "" : "s"} into the trip.
          </p>
          {success.days_skipped > 0 && (
            <p className="font-accent italic text-[12px] text-[hsl(var(--foreground-muted))]">
              {success.days_skipped} template day{success.days_skipped === 1 ? "" : "s"} couldn't land — extend the trip's calendar first.
            </p>
          )}
        </div>
      ) : (
      <form onSubmit={submit} className="space-y-4">
        <Field label="Target trip" required>
          <select
            value={tripId}
            onChange={(e) => setTripId(e.target.value)}
            data-testid="clone-trip-template-trip"
            className="w-full px-3 py-2 rounded-md border border-[hsl(var(--surface-hairline))]/50 bg-white font-serif"
          >
            {trips === null ? (
              <option value="">Loading…</option>
            ) : trips.length === 0 ? (
              <option value="">No other trips in your portfolio</option>
            ) : (
              trips.map((t) => (
                <option key={t.id} value={t.id}>
                  {t.internal_trip_name || t.name || t.id}
                </option>
              ))
            )}
          </select>
          {trips && trips.length === 0 && sourceTripId && (
            <p className="font-accent italic text-[12px] text-[hsl(var(--foreground-muted))] mt-1.5">
              This template came from your only active trip. Create another trip first to clone it forward.
            </p>
          )}
        </Field>
        <Field label="Start at day">
          <input
            type="number"
            min="1"
            value={startDay}
            onChange={(e) => setStartDay(e.target.value)}
            data-testid="clone-trip-template-start-day"
            className="w-full px-3 py-2 rounded-md border border-[hsl(var(--surface-hairline))]/50 bg-white font-serif"
          />
        </Field>
        <label className="flex items-start gap-2 font-serif text-sm text-[hsl(var(--foreground))]">
          <input
            type="checkbox"
            checked={overwrite}
            onChange={(e) => setOverwrite(e.target.checked)}
            data-testid="clone-trip-template-overwrite"
            className="mt-1 accent-[hsl(var(--accent-gold))]"
          />
          <span>
            Replace existing content
            <span className="block font-accent italic text-[12px] text-[hsl(var(--foreground-muted))]">
              When checked, target days' blocks are wiped and the template
              takes over.  Otherwise, the template's items are appended.
            </span>
          </span>
        </label>
        {err && (
          <p className="font-serif text-sm text-[hsl(var(--accent-terracotta,15_70%_55%))]" data-testid="clone-trip-template-error">
            {err}
          </p>
        )}
        <div className="flex items-center justify-end gap-2 pt-2">
          <button
            type="button"
            onClick={onClose}
            className="font-mono text-[10px] uppercase tracking-[0.18em] px-3 py-2 rounded-full text-[hsl(var(--foreground-muted))] hover:text-[hsl(var(--foreground))]"
          >
            Cancel
          </button>
          <PortalButton type="submit" variant="primary" disabled={busy || !tripId} data-testid="clone-trip-template-submit">
            {busy ? "Cloning…" : "Clone to trip"}
            <ChevronRight className="size-3.5" strokeWidth={1.5} />
          </PortalButton>
        </div>
      </form>
      )}
    </DialogShell>
  );
}

// ─── Trip Template · Version History (Wave 31 · Phase 3 · P3) ──────

function TripTemplateVersionDialog({ template, onClose }) {
  const [data, setData] = React.useState(null);
  const [err, setErr] = React.useState("");

  React.useEffect(() => {
    api.get(`/planning-studio/trip-templates/${template.id}/versions`)
      .then(({ data }) => setData(data))
      .catch((e) => setErr(e?.response?.data?.detail || "Couldn't load history."));
  }, [template.id]);

  return (
    <DialogShell
      title={`Version history · ${template.name}`}
      onClose={onClose}
      testid="trip-template-version-dialog"
    >
      <div className="space-y-3 max-h-[60vh] overflow-y-auto">
        {err && <p className="font-accent italic text-[13px] text-[hsl(var(--foreground-muted))]">{err}</p>}
        {!data && !err && <Skeleton className="h-20" />}
        {data && data.versions.length === 0 && (
          <p className="font-accent italic text-[13px] text-[hsl(var(--foreground-muted))]">
            No history yet — this is v1.
          </p>
        )}
        {data && data.versions.map((v, idx) => {
          const isCurrent = v.version === data.current_version;
          return (
            <div
              key={`${v.version}-${idx}`}
              data-testid={`trip-template-version-row-${v.version}`}
              className={`px-4 py-3 rounded-lg border ${
                isCurrent
                  ? "bg-[hsl(var(--accent-gold))]/10 border-[hsl(var(--accent-gold))]/30"
                  : "bg-[hsl(var(--background-paper))]/40 border-transparent"
              }`}
            >
              <div className="flex items-center gap-2 flex-wrap">
                <span className="font-mono text-[11px] uppercase tracking-[0.18em] text-[hsl(var(--foreground))]">
                  v{v.version}
                </span>
                {isCurrent && (
                  <span className="font-mono text-[9px] uppercase tracking-[0.18em] px-2 py-0.5 rounded-full bg-[hsl(var(--accent-gold))]/15 text-[hsl(var(--accent-gold))]">
                    Current
                  </span>
                )}
                <span className="font-accent italic text-[12px] text-[hsl(var(--foreground-muted))]">
                  {v.length_in_days} days
                </span>
                {v.edited_at && (
                  <span className="font-accent italic text-[12px] text-[hsl(var(--foreground-muted))] ml-auto">
                    {new Date(v.edited_at).toLocaleString(undefined, {
                      year: "numeric", month: "short", day: "numeric", hour: "numeric", minute: "2-digit",
                    })}
                  </span>
                )}
              </div>
              {v.note && (
                <p className="font-serif text-[13px] text-[hsl(var(--foreground))] mt-1.5">
                  {v.note}
                </p>
              )}
            </div>
          );
        })}
        <p className="font-accent italic text-[12px] text-[hsl(var(--foreground-muted))] pt-2 border-t border-[hsl(var(--surface-hairline))]/40">
          Trips you've already cloned remain pinned to the version that was applied — refreshing the template never mutates an existing trip.
        </p>
      </div>
    </DialogShell>
  );
}


function RefreshTripTemplateDialog({ template, onClose, onDone }) {
  const [note, setNote] = React.useState("");
  const [busy, setBusy] = React.useState(false);
  const [err, setErr] = React.useState("");
  const [result, setResult] = React.useState(null);

  const submit = async (ev) => {
    ev.preventDefault();
    setBusy(true);
    setErr("");
    try {
      const { data } = await api.post(
        `/planning-studio/trip-templates/${template.id}/refresh-from-trip`,
        { note: note.trim() || undefined }
      );
      setResult({ version: data.version });
      setTimeout(() => onDone?.(), 1400);
    } catch (e) {
      setErr(e?.response?.data?.detail || "Couldn't refresh template.");
    } finally {
      setBusy(false);
    }
  };

  return (
    <DialogShell
      title={`Refresh "${template.name}"`}
      onClose={onClose}
      testid="refresh-trip-template-dialog"
    >
      {result ? (
        <div className="space-y-2 py-2" data-testid="refresh-trip-template-success">
          <p className="font-serif text-sm text-[hsl(var(--foreground))]">
            ✓ Refreshed to v{result.version}.
          </p>
          <p className="font-accent italic text-[12px] text-[hsl(var(--foreground-muted))]">
            Existing cloned trips remain pinned to their original version.
          </p>
        </div>
      ) : (
        <form onSubmit={submit} className="space-y-4">
          <p className="font-accent italic text-[13px] text-[hsl(var(--foreground-muted))]">
            Take a fresh snapshot from the source trip. The version number will increment and your note will appear in the history timeline.
          </p>
          <Field label="What changed (optional)">
            <textarea
              value={note}
              onChange={(e) => setNote(e.target.value)}
              placeholder="e.g., Polished the day 1 anchors and added a new sunset moment."
              rows={3}
              data-testid="refresh-trip-template-note"
              className="w-full px-3 py-2 rounded-md border border-[hsl(var(--surface-hairline))]/50 bg-white font-serif text-[14px] resize-none"
            />
          </Field>
          {err && (
            <p className="font-accent italic text-[12px] text-[hsl(var(--accent-terracotta,15_70%_55%))]">
              {err}
            </p>
          )}
          <div className="flex items-center justify-end gap-2">
            <button
              type="button"
              onClick={onClose}
              className="font-mono text-[10px] uppercase tracking-[0.18em] px-3 py-2 rounded-full text-[hsl(var(--foreground-muted))] hover:text-[hsl(var(--foreground))]"
            >
              Cancel
            </button>
            <PortalButton
              type="submit"
              variant="primary"
              disabled={busy}
              data-testid="refresh-trip-template-submit"
            >
              {busy ? "Refreshing…" : "Refresh template"}
              <RefreshCw className="size-3.5" strokeWidth={1.5} />
            </PortalButton>
          </div>
        </form>
      )}
    </DialogShell>
  );
}


// ─── Day Templates ─────────────────────────────────────────────────

function DayTemplatesView({ onRefresh }) {
  const [items, setItems] = React.useState(null);
  const load = () => {
    api.get("/planning-studio/day-templates")
      .then(({ data }) => setItems(data.day_templates || []))
      .catch(() => setItems([]));
  };
  React.useEffect(load, []);

  const onArchive = async (id) => {
    await api.post(`/planning-studio/day-templates/${id}/archive`);
    load();
    onRefresh?.();
  };
  const onFavorite = async (id, current) => {
    await api.post(`/planning-studio/day-templates/${id}/favorite`,
      { favorited: !current });
    load();
  };
  const onToggleScope = async (id, currentScope) => {
    const next = currentScope === "agency" ? "private" : "agency";
    await api.post(`/planning-studio/day-templates/${id}/scope`, { scope: next });
    load();
    onRefresh?.();
  };

  if (items === null) return <Skeleton className="h-32" />;

  return (
    <div data-testid="planning-studio-day-templates" className="space-y-3">
      <Surface variant="paper" className="p-4 flex items-center gap-3">
        <Compass className="size-4 text-[hsl(var(--accent-gold))]" strokeWidth={1.5} />
        <div className="flex-1">
          <p className="font-serif text-sm text-[hsl(var(--foreground))]">
            Save any day from a live trip as a reusable template.
          </p>
          <p className="font-mono text-[10px] uppercase tracking-[0.18em] text-[hsl(var(--foreground-muted))] mt-1">
            Use the "Save day as template" action inside the itinerary editor.
          </p>
        </div>
      </Surface>
      {items.length === 0 ? (
        <Empty title="No day templates saved yet." icon={<Layers className="size-6" strokeWidth={1.25} />} />
      ) : (
        items.map((t) => {
          const isOwner = t.is_owner !== false;
          const fav = t.effective_favorited ?? t.favorited;
          const isAgency = t.scope === "agency";
          return (
            <div
              key={t.id}
              data-testid={`day-template-row-${t.id}`}
              className="px-4 py-3 rounded-lg bg-white border border-[hsl(var(--surface-hairline))]/40 flex items-center gap-3 flex-wrap"
            >
              <button
                type="button"
                onClick={() => onFavorite(t.id, fav)}
                data-testid={`day-template-fav-${t.id}`}
                aria-label={fav ? "Unfavorite" : "Favorite"}
                className={`size-7 grid place-items-center rounded-full transition-colors ${
                  fav
                    ? "text-[hsl(var(--accent-gold))]"
                    : "text-[hsl(var(--foreground-muted))] hover:text-[hsl(var(--accent-gold))]"
                }`}
              >
                <Star className="size-4" strokeWidth={1.5} fill={fav ? "currentColor" : "none"} />
              </button>
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2 flex-wrap">
                  <p className="font-serif text-sm text-[hsl(var(--foreground))]">{t.name}</p>
                  {!isOwner && t.owner_name && (
                    <span
                      data-testid={`day-template-owner-badge-${t.id}`}
                      className="font-mono text-[9px] uppercase tracking-[0.18em] px-2 py-0.5 rounded-full bg-[hsl(var(--accent-gold))]/12 text-[hsl(var(--accent-gold))] border border-[hsl(var(--accent-gold))]/30"
                    >
                      Agency · {t.owner_name}
                    </span>
                  )}
                  {isOwner && isAgency && (
                    <span
                      data-testid={`day-template-shared-badge-${t.id}`}
                      className="font-mono text-[9px] uppercase tracking-[0.18em] px-2 py-0.5 rounded-full bg-[hsl(var(--foreground))]/8 text-[hsl(var(--foreground-muted))] border border-[hsl(var(--surface-hairline))]/40"
                    >
                      Shared with agency
                    </span>
                  )}
                </div>
                <p className="font-mono text-[10px] uppercase tracking-[0.18em] text-[hsl(var(--foreground-muted))] mt-0.5">
                  {t.destination || "unfiled"} · used {t.usage_count || 0}×
                </p>
              </div>
              {isOwner && (
                <button
                  type="button"
                  onClick={() => onToggleScope(t.id, t.scope)}
                  data-testid={`day-template-scope-${t.id}`}
                  className="font-mono text-[10px] uppercase tracking-[0.18em] px-3 py-1.5 rounded-full text-[hsl(var(--foreground-muted))] hover:text-[hsl(var(--foreground))] transition-colors flex items-center gap-1.5"
                >
                  {isAgency ? "Make private" : "Share with agency"}
                </button>
              )}
              {isOwner && (
                <button
                  type="button"
                  onClick={() => onArchive(t.id)}
                  data-testid={`day-template-archive-${t.id}`}
                  className="font-mono text-[10px] uppercase tracking-[0.18em] px-3 py-1.5 rounded-full text-[hsl(var(--foreground-muted))] hover:text-[hsl(var(--foreground))] transition-colors flex items-center gap-1.5"
                >
                  <ArchiveIcon className="size-3" strokeWidth={1.5} /> Archive
                </button>
              )}
            </div>
          );
        })
      )}
    </div>
  );
}

// ─── Search Results ────────────────────────────────────────────────

function SearchResults({ query, onAddToDay, onRefresh }) {
  const [data, setData] = React.useState(null);
  React.useEffect(() => {
    api.get(`/planning-studio/search?q=${encodeURIComponent(query)}`)
      .then(({ data }) => setData(data))
      .catch(() => setData({ experiences: [], day_templates: [], total: 0 }));
  }, [query]);

  if (data === null) return <Skeleton className="h-32" />;

  return (
    <div className="space-y-6" data-testid="planning-studio-search-results">
      <EditorialRule
        label={`${data.total} result${data.total === 1 ? "" : "s"} for "${query}"`}
      />
      {data.experiences?.length > 0 && (
        <div>
          <p className="font-mono text-[10px] uppercase tracking-[0.18em] text-[hsl(var(--foreground-muted))] mb-3">
            Experiences
          </p>
          <ExperienceGrid
            experiences={data.experiences}
            onAddToDay={onAddToDay}
            onRefresh={onRefresh}
          />
        </div>
      )}
      {data.day_templates?.length > 0 && (
        <div>
          <p className="font-mono text-[10px] uppercase tracking-[0.18em] text-[hsl(var(--foreground-muted))] mb-3">
            Day Templates
          </p>
          {data.day_templates.map((t) => (
            <div
              key={t.id}
              className="px-4 py-3 rounded-lg bg-white border border-[hsl(var(--surface-hairline))]/40 mb-2"
            >
              <p className="font-serif text-sm">{t.name}</p>
            </div>
          ))}
        </div>
      )}
      {data.total === 0 && (
        <Empty title={`Nothing found for "${query}".`} icon={<Search className="size-6" strokeWidth={1.25} />} />
      )}
    </div>
  );
}

// ─── Experience Grid + Card ────────────────────────────────────────

function ExperienceGrid({ experiences, onAddToDay, onRefresh }) {
  if (experiences === null || experiences === undefined) {
    return (
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
        {[0, 1, 2, 3, 4, 5].map((i) => (
          <Skeleton key={i} className="h-44" />
        ))}
      </div>
    );
  }
  if (experiences.length === 0) {
    return <Empty title="Nothing here yet — save your first experience." icon={<LibraryIcon className="size-6" strokeWidth={1.25} />} />;
  }
  return (
    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4" data-testid="planning-studio-experience-grid">
      {experiences.map((e) => (
        <ExperienceCard
          key={e.id}
          experience={e}
          onAddToDay={onAddToDay}
          onRefresh={onRefresh}
        />
      ))}
    </div>
  );
}

function ExperienceCard({ experience, onAddToDay, onRefresh }) {
  const [working, setWorking] = React.useState(false);
  const e = experience;
  const isOwner = e.is_owner !== false;
  const fav = e.effective_favorited ?? e.favorited;
  const isAgency = e.scope === "agency";

  const toggleFav = async () => {
    setWorking(true);
    try {
      await api.post(`/planning-studio/experiences/${e.id}/favorite`, {
        favorited: !fav,
      });
      onRefresh?.();
    } finally {
      setWorking(false);
    }
  };
  const duplicate = async () => {
    setWorking(true);
    try {
      await api.post(`/planning-studio/experiences/${e.id}/duplicate`);
      onRefresh?.();
    } finally {
      setWorking(false);
    }
  };
  const archive = async () => {
    setWorking(true);
    try {
      await api.post(`/planning-studio/experiences/${e.id}/archive`);
      onRefresh?.();
    } finally {
      setWorking(false);
    }
  };
  const toggleScope = async () => {
    setWorking(true);
    try {
      const next = isAgency ? "private" : "agency";
      await api.post(`/planning-studio/experiences/${e.id}/scope`, { scope: next });
      onRefresh?.();
    } finally {
      setWorking(false);
    }
  };

  return (
    <div
      data-testid={`experience-card-${e.id}`}
      className="group rounded-lg bg-white border border-[hsl(var(--surface-hairline))]/40 overflow-hidden flex flex-col transition-shadow hover:shadow-sm"
    >
      {e.image_url ? (
        <div className="aspect-[16/10] bg-[hsl(var(--background-paper))]/40 overflow-hidden">
          <img
            src={e.image_url}
            alt={e.name}
            loading="lazy"
            className="w-full h-full object-cover transition-transform duration-300 group-hover:scale-[1.02]"
          />
        </div>
      ) : null}
      <div className="p-4 flex-1 flex flex-col gap-2">
        <div className="flex items-start justify-between gap-3">
          <div className="min-w-0">
            <p className="font-serif text-[15px] text-[hsl(var(--foreground))] line-clamp-2">
              {e.name}
            </p>
            <div className="flex items-center gap-1.5 flex-wrap mt-1">
              <p className="font-mono text-[10px] uppercase tracking-[0.18em] text-[hsl(var(--foreground-muted))]">
                {(e.item_type || "activity")} · {e.destination || "unfiled"}
              </p>
              {!isOwner && e.owner_name && (
                <span
                  data-testid={`experience-owner-badge-${e.id}`}
                  className="font-mono text-[9px] uppercase tracking-[0.18em] px-2 py-0.5 rounded-full bg-[hsl(var(--accent-gold))]/12 text-[hsl(var(--accent-gold))] border border-[hsl(var(--accent-gold))]/30"
                >
                  Agency · {e.owner_name}
                </span>
              )}
              {isOwner && isAgency && (
                <span
                  data-testid={`experience-shared-badge-${e.id}`}
                  className="font-mono text-[9px] uppercase tracking-[0.18em] px-2 py-0.5 rounded-full bg-[hsl(var(--foreground))]/8 text-[hsl(var(--foreground-muted))] border border-[hsl(var(--surface-hairline))]/40"
                >
                  Shared
                </span>
              )}
            </div>
          </div>
          <button
            type="button"
            onClick={toggleFav}
            disabled={working}
            data-testid={`experience-fav-${e.id}`}
            aria-label={fav ? "Unfavorite" : "Favorite"}
            className={`shrink-0 size-7 rounded-full grid place-items-center transition-colors ${
              fav
                ? "text-[hsl(var(--accent-gold))]"
                : "text-[hsl(var(--foreground-muted))] hover:text-[hsl(var(--accent-gold))]"
            }`}
          >
            <Star className="size-4" strokeWidth={1.5} fill={fav ? "currentColor" : "none"} />
          </button>
        </div>
        {e.description ? (
          <p className="font-serif text-[13px] text-[hsl(var(--foreground-secondary))] line-clamp-2">
            {e.description}
          </p>
        ) : null}
        {(e.tags || []).length > 0 && (
          <div className="flex flex-wrap gap-1.5 pt-1">
            {(e.tags || []).slice(0, 4).map((t) => (
              <span
                key={t}
                className="font-mono text-[9.5px] uppercase tracking-[0.18em] px-2 py-0.5 rounded-full bg-[hsl(var(--background-paper))]/60 text-[hsl(var(--foreground-muted))]"
              >
                {t}
              </span>
            ))}
          </div>
        )}
        <div className="mt-auto pt-3 flex items-center justify-between gap-2">
          <PortalButton
            variant="primary"
            onClick={() => onAddToDay?.({
              id: e.id,
              name: e.name,
              tags: e.tags || [],
              item_type: e.item_type,
              suggested_block: e.suggested_block,
              suggested_time: e.suggested_time,
            })}
            data-testid={`experience-add-${e.id}`}
            disabled={working}
          >
            <Send className="size-3.5" strokeWidth={1.5} />
            <span>Add to Day</span>
          </PortalButton>
          <div className="flex items-center gap-1">
            {isOwner && (
              <button
                type="button"
                onClick={toggleScope}
                disabled={working}
                data-testid={`experience-scope-${e.id}`}
                title={isAgency ? "Make private" : "Share with agency"}
                aria-label={isAgency ? "Make private" : "Share with agency"}
                className={`size-7 rounded-full grid place-items-center transition-colors ${
                  isAgency
                    ? "text-[hsl(var(--accent-gold))] hover:bg-[hsl(var(--background-paper))]/60"
                    : "text-[hsl(var(--foreground-muted))] hover:text-[hsl(var(--foreground))] hover:bg-[hsl(var(--background-paper))]/60"
                }`}
              >
                <Users className="size-3.5" strokeWidth={1.5} />
              </button>
            )}
            {isOwner && (
              <button
                type="button"
                onClick={duplicate}
                disabled={working}
                data-testid={`experience-duplicate-${e.id}`}
                title="Duplicate"
                className="size-7 rounded-full grid place-items-center text-[hsl(var(--foreground-muted))] hover:text-[hsl(var(--foreground))] hover:bg-[hsl(var(--background-paper))]/60 transition-colors"
              >
                <Copy className="size-3.5" strokeWidth={1.5} />
              </button>
            )}
            {isOwner && (
              <button
                type="button"
                onClick={archive}
                disabled={working}
                data-testid={`experience-archive-${e.id}`}
                title="Archive"
                className="size-7 rounded-full grid place-items-center text-[hsl(var(--foreground-muted))] hover:text-[hsl(var(--foreground))] hover:bg-[hsl(var(--background-paper))]/60 transition-colors"
              >
                <ArchiveIcon className="size-3.5" strokeWidth={1.5} />
              </button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

// ─── Dialogs ───────────────────────────────────────────────────────

// Wave 31 · Track B · PI — Smart block suggestion helper.
// Maps experience hints (suggested_block / suggested_time / item_type
// / tags) to one of the four day blocks.  Returns null when no signal
// is strong enough, in which case the dialog falls back to "afternoon".
function suggestBlock(experience) {
  if (!experience) return null;
  const directBlock = (experience.suggested_block || "").toLowerCase();
  if (["morning", "afternoon", "evening", "late_night"].includes(directBlock)) {
    return directBlock;
  }
  const tags = (experience.tags || []).map((t) => (t || "").toLowerCase());
  const has = (kw) => tags.some((t) => t.includes(kw));
  if (has("breakfast") || has("brunch") || has("sunrise") || has("morning") || has("am")) return "morning";
  if (has("lunch")) return "afternoon";
  if (has("dinner") || has("sunset") || has("aperitif") || has("aperitivo") || has("evening")) return "evening";
  if (has("nightlife") || has("after-dark") || has("late") || has("club") || has("bar crawl")) return "late_night";

  // Parse suggested_time like "8:15 PM" or "20:15"
  const raw = (experience.suggested_time || "").trim();
  if (raw) {
    let hour = null;
    const m12 = raw.match(/^(\d{1,2}):?(\d{0,2})?\s*(am|pm)$/i);
    const m24 = raw.match(/^(\d{1,2})(?::(\d{1,2}))?$/);
    if (m12) {
      hour = parseInt(m12[1], 10) % 12;
      if (m12[3].toLowerCase() === "pm") hour += 12;
    } else if (m24) {
      hour = parseInt(m24[1], 10);
    }
    if (hour !== null) {
      if (hour < 12) return "morning";
      if (hour < 17) return "afternoon";
      if (hour < 22) return "evening";
      return "late_night";
    }
  }

  const type = (experience.item_type || "").toLowerCase();
  if (type === "meal") return "afternoon";  // safe meal default
  if (type === "rest") return "afternoon";  // hotel check-in window
  return null;
}

function ExperienceFormDialog({ onClose, onSaved }) {
  const [name, setName] = React.useState("");
  const [destination, setDestination] = React.useState("");
  const [itemType, setItemType] = React.useState("activity");
  const [description, setDescription] = React.useState("");
  const [tags, setTags] = React.useState("");
  const [busy, setBusy] = React.useState(false);
  const [err, setErr] = React.useState("");

  const submit = async (ev) => {
    ev.preventDefault();
    if (!name.trim()) {
      setErr("Name is required.");
      return;
    }
    setBusy(true);
    setErr("");
    try {
      await api.post("/planning-studio/experiences", {
        name: name.trim(),
        destination: destination.trim(),
        item_type: itemType,
        description: description.trim() || null,
        tags: tags.split(",").map((t) => t.trim()).filter(Boolean),
      });
      onSaved?.();
    } catch (e) {
      setErr(e?.response?.data?.detail || "Could not save.");
    } finally {
      setBusy(false);
    }
  };

  return (
    <DialogShell title="New Experience" onClose={onClose} testid="experience-form-dialog">
      <form onSubmit={submit} className="space-y-4">
        <Field label="Name" required>
          <input
            value={name}
            onChange={(e) => setName(e.target.value)}
            data-testid="experience-form-name"
            className="w-full px-3 py-2 rounded-md border border-[hsl(var(--surface-hairline))]/50 bg-white font-serif"
            autoFocus
          />
        </Field>
        <Field label="Destination">
          <input
            value={destination}
            onChange={(e) => setDestination(e.target.value)}
            placeholder="e.g. Paris, Antigua, NYC"
            data-testid="experience-form-destination"
            className="w-full px-3 py-2 rounded-md border border-[hsl(var(--surface-hairline))]/50 bg-white font-serif"
          />
        </Field>
        <Field label="Type">
          <select
            value={itemType}
            onChange={(e) => setItemType(e.target.value)}
            data-testid="experience-form-type"
            className="w-full px-3 py-2 rounded-md border border-[hsl(var(--surface-hairline))]/50 bg-white font-serif"
          >
            <option value="meal">Meal</option>
            <option value="activity">Activity</option>
            <option value="rest">Rest / Hotel</option>
            <option value="transport">Transport</option>
            <option value="custom">Custom</option>
          </select>
        </Field>
        <Field label="Description">
          <textarea
            value={description}
            onChange={(e) => setDescription(e.target.value)}
            data-testid="experience-form-description"
            rows={3}
            className="w-full px-3 py-2 rounded-md border border-[hsl(var(--surface-hairline))]/50 bg-white font-serif"
          />
        </Field>
        <Field label="Tags (comma-separated)">
          <input
            value={tags}
            onChange={(e) => setTags(e.target.value)}
            data-testid="experience-form-tags"
            className="w-full px-3 py-2 rounded-md border border-[hsl(var(--surface-hairline))]/50 bg-white font-serif"
          />
        </Field>
        {err && (
          <p className="font-serif text-sm text-[hsl(var(--accent-terracotta,15_70%_55%))]" data-testid="experience-form-error">
            {err}
          </p>
        )}
        <div className="flex items-center justify-end gap-2 pt-2">
          <button
            type="button"
            onClick={onClose}
            className="font-mono text-[10px] uppercase tracking-[0.18em] px-3 py-2 rounded-full text-[hsl(var(--foreground-muted))] hover:text-[hsl(var(--foreground))] transition-colors"
          >
            Cancel
          </button>
          <PortalButton
            type="submit"
            variant="primary"
            disabled={busy}
            data-testid="experience-form-submit"
          >
            {busy ? "Saving…" : "Save"}
          </PortalButton>
        </div>
      </form>
    </DialogShell>
  );
}

function AddToDayDialog({ experience, onClose, onAdded }) {
  const [trips, setTrips] = React.useState(null);
  const [tripId, setTripId] = React.useState("");
  const [dayNumber, setDayNumber] = React.useState(1);
  // Wave 31 · Track B · PI — Smart block suggestion.  When the
  // experience carries a `suggested_block`, a `suggested_time` (e.g.
  // 8:15 PM), an `item_type` (transport/meal/rest), or tags like
  // "breakfast", "sunset", "nightlife", the dialog auto-selects the
  // best block.  Advisor can override with a single click.
  const suggested = React.useMemo(() => suggestBlock(experience), [experience]);
  const [block, setBlock] = React.useState(suggested || "afternoon");
  const [busy, setBusy] = React.useState(false);
  const [err, setErr] = React.useState("");
  const [days, setDays] = React.useState(null);

  React.useEffect(() => {
    api.get("/travels/trips")
      .then(({ data }) => {
        const list = Array.isArray(data) ? data : data.trips || [];
        setTrips(list);
        if (list.length > 0 && !tripId) setTripId(list[0].id);
      })
      .catch(() => setTrips([]));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  React.useEffect(() => {
    if (!tripId) {
      setDays(null);
      return;
    }
    api.get(`/itinerary-engine/trips/${tripId}/days`)
      .then(({ data }) => setDays(data.days || []))
      .catch(() => setDays([]));
  }, [tripId]);

  const submit = async (ev) => {
    ev.preventDefault();
    if (!tripId) {
      setErr("Pick a trip.");
      return;
    }
    setBusy(true);
    setErr("");
    try {
      await api.post(`/planning-studio/experiences/${experience.id}/add-to-day`, {
        trip_id: tripId,
        day_number: Number(dayNumber),
        block,
      });
      onAdded?.();
    } catch (e) {
      setErr(e?.response?.data?.detail || "Could not add.");
    } finally {
      setBusy(false);
    }
  };

  return (
    <DialogShell
      title={`Add "${experience.name}" to a day`}
      onClose={onClose}
      testid="add-to-day-dialog"
    >
      <form onSubmit={submit} className="space-y-4">
        <Field label="Trip" required>
          <select
            value={tripId}
            onChange={(e) => setTripId(e.target.value)}
            data-testid="add-to-day-trip"
            className="w-full px-3 py-2 rounded-md border border-[hsl(var(--surface-hairline))]/50 bg-white font-serif"
          >
            {trips === null ? (
              <option value="">Loading…</option>
            ) : trips.length === 0 ? (
              <option value="">No trips found</option>
            ) : (
              trips.map((t) => (
                <option key={t.id} value={t.id}>
                  {t.internal_trip_name || t.name || t.id}
                </option>
              ))
            )}
          </select>
        </Field>
        <Field label="Day" required>
          <select
            value={dayNumber}
            onChange={(e) => setDayNumber(e.target.value)}
            data-testid="add-to-day-day"
            className="w-full px-3 py-2 rounded-md border border-[hsl(var(--surface-hairline))]/50 bg-white font-serif"
          >
            {days === null ? (
              <option value="">Loading…</option>
            ) : days.length === 0 ? (
              <option value="">No days available</option>
            ) : (
              days.map((d) => (
                <option key={d.day_number} value={d.day_number}>
                  Day {d.day_number} · {d.date || ""}
                </option>
              ))
            )}
          </select>
        </Field>
        <Field label="Block" required>
          <div className="flex items-center gap-2">
            <select
              value={block}
              onChange={(e) => setBlock(e.target.value)}
              data-testid="add-to-day-block"
              className="flex-1 px-3 py-2 rounded-md border border-[hsl(var(--surface-hairline))]/50 bg-white font-serif"
            >
              <option value="morning">Morning</option>
              <option value="afternoon">Afternoon</option>
              <option value="evening">Evening</option>
              <option value="late_night">Late night</option>
            </select>
            {suggested && (
              <span
                data-testid="add-to-day-block-suggested"
                title={`Suggested from ${experience?.suggested_block ? "saved block hint" : experience?.suggested_time ? "saved time hint" : "tags / type"}`}
                className={`inline-flex items-center gap-1.5 font-mono text-[10px] uppercase tracking-[0.18em] px-2.5 py-1 rounded-full transition-colors ${
                  block === suggested
                    ? "bg-[hsl(var(--accent-gold))]/12 text-[hsl(var(--accent-gold))] border border-[hsl(var(--accent-gold))]/30"
                    : "text-[hsl(var(--foreground-muted))] hover:text-[hsl(var(--foreground))] cursor-pointer border border-transparent"
                }`}
                onClick={() => setBlock(suggested)}
              >
                <Sparkles className="size-3" strokeWidth={1.5} />
                Suggested
              </span>
            )}
          </div>
        </Field>
        {err && (
          <p className="font-serif text-sm text-[hsl(var(--accent-terracotta,15_70%_55%))]" data-testid="add-to-day-error">
            {err}
          </p>
        )}
        <div className="flex items-center justify-end gap-2 pt-2">
          <button
            type="button"
            onClick={onClose}
            className="font-mono text-[10px] uppercase tracking-[0.18em] px-3 py-2 rounded-full text-[hsl(var(--foreground-muted))] hover:text-[hsl(var(--foreground))] transition-colors"
          >
            Cancel
          </button>
          <PortalButton
            type="submit"
            variant="primary"
            disabled={busy}
            data-testid="add-to-day-submit"
          >
            {busy ? "Adding…" : "Add to Day"}
            <ChevronRight className="size-3.5" strokeWidth={1.5} />
          </PortalButton>
        </div>
      </form>
    </DialogShell>
  );
}

function DialogShell({ title, onClose, testid, children }) {
  React.useEffect(() => {
    const onEsc = (e) => {
      if (e.key === "Escape") onClose?.();
    };
    window.addEventListener("keydown", onEsc);
    return () => window.removeEventListener("keydown", onEsc);
  }, [onClose]);

  return (
    <div
      className="fixed inset-0 z-50 grid place-items-center bg-black/40 px-4"
      data-testid={testid}
      onClick={(e) => {
        if (e.target === e.currentTarget) onClose?.();
      }}
    >
      <div className="w-full max-w-lg rounded-xl bg-white shadow-xl overflow-hidden border border-[hsl(var(--surface-hairline))]/40">
        <div className="flex items-center justify-between px-5 py-4 border-b border-[hsl(var(--surface-hairline))]/40">
          <h2 className="font-serif text-lg">{title}</h2>
          <button
            type="button"
            onClick={onClose}
            data-testid={`${testid}-close`}
            aria-label="Close"
            className="size-7 grid place-items-center text-[hsl(var(--foreground-muted))] hover:text-[hsl(var(--foreground))]"
          >
            <X className="size-4" strokeWidth={1.5} />
          </button>
        </div>
        <div className="p-5 max-h-[70vh] overflow-y-auto">{children}</div>
      </div>
    </div>
  );
}

function Field({ label, required, children }) {
  return (
    <label className="block">
      <span className="font-mono text-[10px] uppercase tracking-[0.18em] text-[hsl(var(--foreground-muted))] block mb-1.5">
        {label}
        {required ? " *" : ""}
      </span>
      {children}
    </label>
  );
}

export default PlanningStudioLibrary;
